import { useCallback } from 'react'

import { useDispatch } from 'react-redux'
import { browserHistory } from 'react-router'

import type { DashboardActionMeta } from '@features/my-gousto-page'

import { startOneOffOrder } from 'actions/oneOffOrder'
import { client as clientRoutes } from 'config/routes'
import { ONE_OFF_ORDER_ORIGIN } from 'reducers/oneOffOrderOrigin'

import { GOUSTO_ANYTIME_INTENTS, RESUBSCRIBE_URL } from './intents'

type UseGoustoAnytimeIntentHandlerResult = {
  onAction: (intent: string, meta?: DashboardActionMeta) => Promise<void>
}

// `isEmptyState` tags a one-off started from the empty-state dashboard. Any section's CTA inherits
// it, so the trigger survives the hero component changing.
export function useGoustoAnytimeIntentHandler(
  isEmptyState = false,
): UseGoustoAnytimeIntentHandlerResult {
  const dispatch = useDispatch()

  const onAction = useCallback(
    async (intent: string, meta?: DashboardActionMeta): Promise<void> => {
      switch (intent) {
        case GOUSTO_ANYTIME_INTENTS.startOneOffOrder:
          await dispatch(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            startOneOffOrder({
              origin: isEmptyState ? ONE_OFF_ORDER_ORIGIN.emptyStateDashboard : null,
            }) as any,
          )

          return

        case GOUSTO_ANYTIME_INTENTS.reactivateSubscription:
          browserHistory.push(RESUBSCRIBE_URL)

          return

        case GOUSTO_ANYTIME_INTENTS.editRecipes:
          if (meta?.orderId) {
            browserHistory.push(`${clientRoutes.menu}/${meta.orderId}`)
          }

          return

        case GOUSTO_ANYTIME_INTENTS.viewCollection:
          if (meta?.collectionSlug) {
            browserHistory.push(`${clientRoutes.menu}?collection=${meta.collectionSlug}`)
          }

          return

        case GOUSTO_ANYTIME_INTENTS.addToOrder:
          // Box-scoped: opens the target order's menu with the collection open.
          if (meta?.orderId && meta?.collectionSlug) {
            browserHistory.push(
              `${clientRoutes.menu}/${meta.orderId}?collection=${meta.collectionSlug}`,
            )
          }

          return

        case GOUSTO_ANYTIME_INTENTS.pastDeliveries:
          browserHistory.push(clientRoutes.myDeliveries)

          return

        case GOUSTO_ANYTIME_INTENTS.getHelp:
          if (meta?.orderId) {
            browserHistory.push(`${clientRoutes.getHelp.index}?orderId=${meta.orderId}`)
          }

          return

        case GOUSTO_ANYTIME_INTENTS.viewCookbookRecipe:
          // Cookbook lives outside the SPA router; recipeUrl already has a leading slash.
          if (meta?.recipeUrl) {
            window.location.assign(`${clientRoutes.cookbookAllRecipes}${meta.recipeUrl}`)
          }

          return

        default:
          return
      }
    },
    [dispatch, isEmptyState],
  )

  return { onAction }
}
